import React, { useEffect, useMemo, useRef, useState } from "react";
import { Sheet, SheetContent, SheetTitle } from "@/shadcn/components/ui/sheet";
import Checkbox from "@/components/common/Checkbox";
import { useNearByFilters, useI18N } from "common/hooks";
import { IoArrowBackOutline } from "react-icons/io5";
import { bucketUrlPrefix } from "common/config";
import { useDarkMode } from "@/hooks/useDarkMode";
import { StandardButton } from "@/components/common/StandardButton";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/shadcn/components/ui/accordion";
import type { FilterSectionResult } from "common/types";
import AccessibilityCollapse from "@/components/FiltersSheet/components/AccessibilityCollapse";
import { sectionPanel, selectionOf, withSelection } from "@/lib/accessibilityFilterState";
import { encodeSelection, type SelectionKey } from "common/utils/accessibilitySelection";

type NearbyFiltersSheetProps = {
  open: boolean;
  onClose: () => void;
  nearbyFilterAccessibility: any[];
  /** When non-empty, the section list replaces `nearbyFilterAccessibility`. */
  nearbyFilterSections?: FilterSectionResult[];
  accessibilityFeaturesRequirementsHiddenIds: any[];
  accessibilityFeaturesRequirementsShowHiddenById: string;
};

function NearbyFiltersSheet({
  open,
  onClose,
  nearbyFilterAccessibility,
  nearbyFilterSections,
  accessibilityFeaturesRequirementsHiddenIds,
  accessibilityFeaturesRequirementsShowHiddenById,
}: NearbyFiltersSheetProps) {
  const i18n = useI18N();
  const isDark = useDarkMode();

  const { filtersState, setFiltersState } = useNearByFilters();
  const [statusMessage, setStatusMessage] = useState("");

  // An empty list means nothing nearby recorded a filter feature; the full list beats an empty sheet.
  const sectionFilter = useMemo(
    () => (nearbyFilterSections && nearbyFilterSections.length > 0 ? sectionPanel(nearbyFilterSections) : null),
    [nearbyFilterSections],
  );
  const [sectionKeys, setSectionKeys] = useState<SelectionKey[]>([]);
  const [seededOpen, setSeededOpen] = useState(false);
  // Ticks stay local until Apply, starting again from the shared selection on every open.
  if (open !== seededOpen) {
    setSeededOpen(open);
    if (open) setSectionKeys(selectionOf(filtersState));
  }

  const [localBusinessTypes, setLocalBusinessTypes] = useState<string[]>();
  const [localAccessibilityOptions, setLocalAccessibilityOptions] = useState<
    number[]
  >([]);
  const [showAllTags, setShowAllTags] = useState<Record<number, boolean>>({});
  const [allSubItemsSelected, setAllSubItemsSelected] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState<string | null>(
    null,
  );
  const parentIdRef = useRef<number>(
    Number(accessibilityFeaturesRequirementsShowHiddenById),
  );

  useEffect(() => {
    setLocalBusinessTypes(filtersState.selectedBusinessTypes);
  }, [filtersState.selectedBusinessTypes]);

  useEffect(() => {
    setLocalAccessibilityOptions(filtersState.accessibilityOptions);
  }, [filtersState.accessibilityOptions]);

  // Single comprehensive effect to manage sub-item selection state
  useEffect(() => {
    const parentId = parentIdRef.current;
    const subOptionIds = accessibilityFeaturesRequirementsHiddenIds.map((id) =>
      Number(id),
    );

    // Check if parent is selected
    const hasParent = localAccessibilityOptions.includes(parentId);

    // Check if all sub-options are selected
    const allSelected =
      hasParent &&
      subOptionIds.every((id) => localAccessibilityOptions.includes(id));

    // Update allSubItemsSelected state only if it changed
    if (allSelected !== allSubItemsSelected) {
      setAllSubItemsSelected(allSelected);
    }

    // Log for debugging
    console.log("Current selected options:", localAccessibilityOptions);
    console.log("All sub-items selected:", allSelected);
  }, [localAccessibilityOptions, accessibilityFeaturesRequirementsHiddenIds]);

  // Helper function to ensure unique values
  const updateAccessibilityOptions = (newOptions: number[]) => {
    // Ensure we only have unique values
    const uniqueOptions = [...new Set(newOptions)];
    console.log("Setting new options:", uniqueOptions);
    setLocalAccessibilityOptions(uniqueOptions);
  };

  const handleSubOptionClick = (tagId: number, isChecked: boolean) => {
    const currentOptions = [...localAccessibilityOptions];

    if (isChecked) {
      // Remove this sub-option
      const newOptions = currentOptions.filter((id) => id !== tagId);
      console.log("Removing sub-option:", tagId, "New options:", newOptions);
      updateAccessibilityOptions(newOptions);
    } else {
      // Add this sub-option if not already present
      if (!currentOptions.includes(tagId)) {
        const newOptions = [...currentOptions, tagId];
        console.log("Adding sub-option:", tagId, "New options:", newOptions);
        updateAccessibilityOptions(newOptions);
      }
    }
  };

  const handleAllClick = (isChecked: boolean) => {
    const parentId = parentIdRef.current;
    const subOptionIds = accessibilityFeaturesRequirementsHiddenIds.map((id) =>
      Number(id),
    );
    const currentOptions = [...localAccessibilityOptions];

    if (isChecked) {
      // Uncheck all sub-options but keep parent and other selections
      console.log("Unchecking all sub-options");
      const newOptions = currentOptions.filter(
        (id) => !subOptionIds.includes(id) || id === parentId,
      );
      console.log("New options after unchecking all:", newOptions);
      updateAccessibilityOptions(newOptions);
    } else {
      // Check all sub-options while preserving other selections
      console.log("Checking all sub-options");
      // First ensure parent is included
      const newOptions = currentOptions.includes(parentId)
        ? [...currentOptions]
        : [...currentOptions, parentId];

      // Then add any missing sub-options
      const optionsWithSubItems = [...newOptions];
      subOptionIds.forEach((id) => {
        if (!optionsWithSubItems.includes(id)) {
          optionsWithSubItems.push(id);
        }
      });

      console.log("New options after checking all:", optionsWithSubItems);
      updateAccessibilityOptions(optionsWithSubItems);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleClearAll = () => {
    if (sectionFilter) {
      setSectionKeys([]);
      setStatusMessage(i18n.get("filtersCleared"));
      setFiltersState((prev) => ({
        ...withSelection(prev, []),
        openingHoursSelected: false,
        isFiltersApplied: false,
        filtersApplied: null,
        selectedBusinessTypes: [],
      }));
      return;
    }
    setLocalAccessibilityOptions([]);
    setAllSubItemsSelected(false);

    console.log("Clearing all selections");
    setStatusMessage(i18n.get("filtersCleared") || "All filters cleared");

    setFiltersState((prev) => {
      return {
        ...prev,
        openingHoursSelected: false,
        accessibilityOptions: [],
        isFiltersApplied: false,
        filtersApplied: null,
        selectedBusinessTypes: [],
      };
    });
  };

  const applySectionFilters = () => {
    const types = (localBusinessTypes ?? []).join("|");
    const { accessibilities } = encodeSelection(sectionKeys);
    const showVenueOptions = filtersState?.openingHoursSelected ? "Open Now" : "All";
    const isFiltersApplied =
      types.length > 0 || accessibilities.length > 0 || !!filtersState?.openingHoursSelected;

    setFiltersState((prev) => ({
      ...withSelection(prev, sectionKeys),
      isFiltersApplied,
      filtersApplied: isFiltersApplied ? { types, accessibilities, showVenueOptions } : null,
      selectedBusinessTypes: localBusinessTypes ?? [],
    }));
    setStatusMessage(i18n.get(isFiltersApplied ? "filtersApplied" : "noFiltersApplied"));
    handleClose();
  };

  const handleSetFilters = () => {
    if (sectionFilter) {
      applySectionFilters();
      return;
    }
    const types = localBusinessTypes.join("|");
    const accessibilities = localAccessibilityOptions
      ?.map((o) => o.toString())
      .join("|");
    const showVenueOptions = filtersState?.openingHoursSelected
      ? "Open Now"
      : "All";

    console.log("Applying filters with options:", localAccessibilityOptions);

    if (
      types.length <= 0 &&
      accessibilities.length <= 0 &&
      !filtersState?.openingHoursSelected
    ) {
      setFiltersState({
        ...filtersState,
        isFiltersApplied: false,
        filtersApplied: null,
        accessibilityOptions: [], // Explicitly clear
      });
      setStatusMessage(i18n.get("noFiltersApplied") || "No filters applied");
    } else {
      setFiltersState({
        ...filtersState,
        isFiltersApplied: true,
        filtersApplied: { types, accessibilities, showVenueOptions },
        selectedBusinessTypes: localBusinessTypes,
        accessibilityOptions: localAccessibilityOptions, // Explicitly include
      });
      setStatusMessage(i18n.get("filtersApplied") || "Filters applied");
    }

    handleClose();
  };

  const isSelected = (id: number) => {
    return localAccessibilityOptions?.includes(id) || false;
  };

  const firstFocusFilterRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (open && firstFocusFilterRef.current) {
      console.log("open", open);
      // Delay focusing to ensure the element is rendered
      setTimeout(() => {
        console.log("drawerMenuOpen focus set");

        firstFocusFilterRef.current.focus();
      }, 1000);
    }
  }, [open]); // Trigger the effect when the drawer menu opens

  const setSelectedOptions = (options: number) => {
    const parentId = parentIdRef.current;
    const subOptionIds = accessibilityFeaturesRequirementsHiddenIds.map((id) =>
      Number(id),
    );
    const currentOptions = [...localAccessibilityOptions];

    if (!currentOptions.includes(options)) {
      console.log("Adding option:", options);

      // If this is the parent option being checked, add all sub-options as well
      if (options === parentId) {
        console.log("Parent option checked, adding all sub-options");
        const newOptions = [...currentOptions, options, ...subOptionIds];
        updateAccessibilityOptions(newOptions);
        setAllSubItemsSelected(true);
      } else {
        // Just add this specific option
        updateAccessibilityOptions([...currentOptions, options]);
      }
    } else {
      console.log("Removing option:", options);

      if (options === parentId) {
        // When unchecking parent, remove all sub-options
        setAllSubItemsSelected(false);
        const tagsIdsSet = new Set(subOptionIds);
        const newOptions = currentOptions.filter(
          (option) => !tagsIdsSet.has(option) && option !== options,
        );
        updateAccessibilityOptions(newOptions);
      } else {
        // Just remove this specific option
        const newOptions = currentOptions.filter((o) => o !== options);
        updateAccessibilityOptions(newOptions);
      }
    }
  };

  const isAnyOneSelected = (tags: any[]) => {
    const currentIds = tags.map((o) => o.id);
    return localAccessibilityOptions.some((id) => currentIds.includes(id));
  };

  const toggleShowAllTags = (categoryId: number) => {
    setShowAllTags((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  return (
    <Sheet open={open} onOpenChange={() => handleClose()}>
      <SheetContent
        showClose={false}
        side="left"
        className="w-full bg-bg-brand-secondary-default pl-4 flex flex-col h-screen"
      >
        <SheetTitle className="sr-only">Filters Sheet</SheetTitle>
        <div className="flex flex-col h-full overflow-hidden mb-10">
          {/* Add a visually hidden live region to announce filter status changes */}
          <div aria-live="polite" className="sr-only" role="status">
            {statusMessage}
          </div>
          <StandardButton
            variant="backListView"
            onClick={() => handleClose()}
            className="flex flex-row justify-start items-center"
            aria-label={`Back to ${i18n.get("listview")}`}
          >
            <IoArrowBackOutline size={24} aria-hidden="true" focusable="false" />
            <div className="ml-2 body-l-semibold text-fg-neutral-strong">
              {i18n.get("listview")}
            </div>
          </StandardButton>
          <section className="mt-[20px]">
            <h2 className="text-fg-neutral-strong title-s-semibold mb-4">
              <span
                tabIndex={-1}
                ref={firstFocusFilterRef} // Attach the ref here
              >
                {i18n.get("filters")}
              </span>
            </h2>
          </section>

          {/* Main content area - always scrollable */}
          <div className="flex-1 overflow-y-auto">
            <section className="">
              {sectionFilter?.preparedData.map((section) =>
                section.accessibility_tags.length > 0 ? (
                  <AccessibilityCollapse
                    key={section.id}
                    label={section.name}
                    sectionId={section.id}
                    kind={section.kind}
                    tagsToShow={section.accessibility_tags}
                    tagSections={sectionFilter.tagSections}
                    allSections={sectionFilter.allSections}
                    tagsToAdd={sectionFilter.tagsToAdd}
                    complexSectionIds={sectionFilter.complexSectionIds}
                    headingLevel={2}
                    keys={sectionKeys}
                    onKeysChange={setSectionKeys}
                  />
                ) : null,
              )}
              {!sectionFilter && nearbyFilterAccessibility
                ?.filter((category) => category.accessibility_tags?.length > 0)
                .map((category) => {
                  // Filter out tags that are in accessibilityFeaturesRequirementsHiddenIds
                  const filteredTags = category.accessibility_tags.filter(
                    (tag) =>
                      !accessibilityFeaturesRequirementsHiddenIds.some(
                        (id) => Number(id) === tag.id,
                      ),
                  );

                  const actualTagsToShow = showAllTags[category.id]
                    ? filteredTags
                    : filteredTags.slice(0, 5);

                  return (
                    <Accordion
                      key={category.id}
                      type="single"
                      collapsible
                      value={expandedAccordion}
                      onValueChange={(value) =>
                        setExpandedAccordion(value as string)
                      }
                    >
                      <AccordionItem
                        value={`category-${category.id}`}
                        className="border-none"
                      >
                        <AccordionTrigger
                          className={`p-4 flex items-center cursor-pointer ${
                            !isAnyOneSelected(filteredTags)
                              ? "bg-container-brand-minimal"
                              : "bg-container-brand-minimal"
                          }`}
                          aria-label={`${category.name} accessibility filters`}
                        >
                          <div className="flex items-center gap-2 flex-grow">
                            {category.image && (
                              <img
                                src={
                                  bucketUrlPrefix +
                                  (isDark
                                    ? category.image_dark || category.image
                                    : category.image)
                                }
                                alt=""
                                className="w-6 h-6"
                                aria-hidden="true"
                              />
                            )}
                            <div className="text-fg-neutral-strong title-s-semibold text-left">
                              {category.name}
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="overflow-visible">
                          <ul
                            id={`category-panel-${category.id}`}
                            className="p-4 flex flex-col gap-4"
                            role="region"
                            aria-labelledby={`category-header-${category.id}`}
                          >
                            {actualTagsToShow.map((tag) => {
                              // Skip rendering if tag id is in accessibilityFeaturesRequirementsHiddenIds
                              if (
                                accessibilityFeaturesRequirementsHiddenIds.some(
                                  (id) => Number(id) === tag.id,
                                )
                              ) {
                                return null;
                              }
                              return (
                                <div key={tag.id}>
                                  <Checkbox
                                    checked={isSelected(tag.id)}
                                    onCheck={() => setSelectedOptions(tag.id)}
                                    label={tag.name}
                                    aria-label={`${tag.name}, ${isSelected(tag.id) ? "selected" : "not selected"}`}
                                    tabIndex={
                                      expandedAccordion ===
                                      `category-${category.id}`
                                        ? 0
                                        : -1
                                    }
                                  />
                                  {tag.id ===
                                    Number(
                                      accessibilityFeaturesRequirementsShowHiddenById,
                                    ) &&
                                    localAccessibilityOptions.includes(
                                      tag.id,
                                    ) && (
                                      <div key={tag.id + "321"}>
                                        <p className="mt-2 ml-6">
                                          {i18n.get("wouldAnyOfTheseAlsoWork")}
                                        </p>
                                        <Checkbox
                                          className="mt-2 ml-6"
                                          key="all"
                                          label={i18n.get("all")}
                                          checked={allSubItemsSelected}
                                          onCheck={() =>
                                            handleAllClick(allSubItemsSelected)
                                          }
                                          aria-label={`Select all options, ${allSubItemsSelected ? "currently selected" : "currently not selected"}`}
                                          tabIndex={
                                            expandedAccordion ===
                                            `category-${category.id}`
                                              ? 0
                                              : -1
                                          }
                                        />
                                        {accessibilityFeaturesRequirementsHiddenIds.map(
                                          (tagId) => {
                                            // Find the tag object that matches this ID
                                            const matchingTag =
                                              category.accessibility_tags.find(
                                                (tag) =>
                                                  tag.id === Number(tagId),
                                              );
                                            const numTagId = Number(tagId);
                                            const isChecked =
                                              localAccessibilityOptions.includes(
                                                numTagId,
                                              );

                                            return (
                                              <Checkbox
                                                className="mt-2 ml-12"
                                                key={tagId}
                                                label={
                                                  matchingTag
                                                    ? matchingTag.name
                                                    : tagId.toString()
                                                }
                                                checked={isChecked}
                                                onCheck={() =>
                                                  handleSubOptionClick(
                                                    numTagId,
                                                    isChecked,
                                                  )
                                                }
                                                aria-label={`${matchingTag ? matchingTag.name : tagId.toString()}, ${isChecked ? "selected" : "not selected"}`}
                                                tabIndex={
                                                  expandedAccordion ===
                                                  `category-${category.id}`
                                                    ? 0
                                                    : -1
                                                }
                                              />
                                            );
                                          },
                                        )}
                                      </div>
                                    )}
                                </div>
                              );
                            })}
                          </ul>
                          {category.accessibility_tags.length > 5 && (
                            <StandardButton
                              tabIndex={
                                expandedAccordion === `category-${category.id}`
                                  ? 0
                                  : -1
                              }
                              onClick={() => toggleShowAllTags(category.id)}
                              variant="blueLink"
                              className="ml-4 mb-4"
                              aria-expanded={!!showAllTags[category.id]}
                              aria-controls={`category-panel-${category.id}`}
                            >
                              {!showAllTags[category.id]
                                ? i18n.get("showMore")
                                : i18n.get("showLess")}
                            </StandardButton>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  );
                })}
            </section>
          </div>
          <div className="absolute left-0 right-0 bottom-0 flex flex-row justify-between items-center px-[20px] bg-bg-brand-secondary-default py-4">
            <StandardButton
              variant="link"
              className="text-links title-s-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring-default)]"
              onClick={handleClearAll}
            >
              {i18n.get("clearAll")}
            </StandardButton>
            <StandardButton
              onClick={handleSetFilters}
              variant="primary"
              size="lg"
              className="px-6 py-3 rounded-xl text-white bg-fg-neutral-strong title-s-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring-default)]"
            >
              {i18n.get("applyFilters")}
            </StandardButton>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default NearbyFiltersSheet;
